import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Heart, CreditCard, Sparkles } from "lucide-react";
import SectionTitle from "../components/SectionTitle";
import FloatingPetals from "../components/FloatingPetals";
import Reveal from "../components/Reveal";
import BookLink from "../components/BookLink";
import useSeo from "../hooks/useSeo";
import { pricing, packages, policies, business } from "../data/content";

export default function Pricing() {
  useSeo({
    title: `Pricing | Soft Petal Electrolysis, ${business.city}, ${business.state}`,
    description: `Transparent electrolysis pricing at Soft Petal Electrolysis in ${business.city}, ${business.state}. Single sessions from $45 and multi-session packages up to 15 sessions with savings.`,
    path: "/pricing",
  });

  return (
    <div>
      <section className="watercolor-blush py-16 lg:py-14 relative overflow-hidden">
        <FloatingPetals count={5} />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <SectionTitle
            eyebrow="Pricing"
            title="Simple, transparent"
            scriptWord="pricing"
            subtitle="Your recommended appointment length will depend on the treatment area, hair density, and your individual goals. We will help you choose the most appropriate option during your consultation."
          />
        </div>
      </section>

      {/* Single-session pricing */}
      <section className="py-12 lg:py-14">
        <div className="max-w-4xl mx-auto px-6 sm:px-10">
          <SectionTitle
            eyebrow="Single Sessions"
            title="Book by the"
            scriptWord="minute"
            align="left"
          />
          <div className="mt-10 rounded-3xl bg-warm-white border border-pale-rose/50 shadow-soft overflow-hidden" data-testid="pricing-table">
            {pricing.map((row, i) => (
              <div
                key={row.label}
                className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 sm:px-10 py-5 ${
                  i !== pricing.length - 1 ? "border-b border-pale-rose/50" : ""
                }`}
                data-testid={`price-row-${i}`}
              >
                <div>
                  <h3 className="font-serif-display text-xl leading-tight">{row.label}</h3>
                  <p className="mt-1 text-xs font-body tracking-[0.22em] uppercase text-petal flex items-center gap-1.5">
                    <Clock size={12} /> {row.duration}
                  </p>
                </div>
                <div className="font-serif-display text-2xl text-petal">{row.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages table */}
      <section className="py-14 lg:py-16 bg-blush/40" data-testid="packages-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <SectionTitle
            eyebrow="Save More"
            title="Electrolysis"
            scriptWord="Packages"
            subtitle="Commit to your journey and save. Packages are the best value for consistent, long-term results."
          />

          <Reveal className="mt-12 overflow-x-auto rounded-3xl bg-warm-white border border-pale-rose/50 shadow-soft">
            <table className="w-full text-left border-collapse min-w-[620px]" data-testid="packages-table">
              <thead>
                <tr className="border-b border-pale-rose/50">
                  {packages.headers.map((h, i) => (
                    <th
                      key={h}
                      scope="col"
                      className={`px-4 sm:px-6 py-5 font-serif-display italic text-base sm:text-lg text-charcoal ${
                        i === 0 ? "text-left" : "text-center"
                      } ${i >= 2 ? "bg-blush/30" : ""}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {packages.rows.map((r, idx) => (
                  <tr
                    key={r.length}
                    className={idx !== packages.rows.length - 1 ? "border-b border-pale-rose/40" : ""}
                    data-testid={`pkg-row-${idx}`}
                  >
                    <th
                      scope="row"
                      className="px-4 sm:px-6 py-6 font-serif-display italic text-lg text-petal text-left"
                    >
                      {r.length}
                    </th>
                    <td className="px-4 sm:px-6 py-6 text-center">
                      <div className="font-serif-display text-2xl text-charcoal">{r.single}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-6 text-center bg-blush/20">
                      <div className="font-serif-display text-2xl text-charcoal">{r.five}</div>
                      <div className="font-body text-xs text-petal mt-1">{r.fiveSave}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-6 text-center bg-blush/30">
                      <div className="font-serif-display text-2xl text-charcoal">{r.ten}</div>
                      <div className="font-body text-xs text-petal mt-1">{r.tenSave}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-6 text-center bg-blush/40 relative">
                      <div className="font-serif-display text-2xl text-charcoal">{r.fifteen}</div>
                      <div className="font-body text-xs text-petal mt-1">{r.fifteenSave}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 font-body text-sm text-charcoal-soft">
            <span className="inline-flex items-center gap-2">
              <CreditCard size={14} className="text-petal" /> Credit card fees apply
            </span>
            <span className="inline-flex items-center gap-2">
              <Heart size={14} className="text-petal fill-petal" /> Tips are appreciated
            </span>
            <span className="inline-flex items-center gap-2">
              <Sparkles size={14} className="text-petal" /> Prices in USD
            </span>
          </div>
        </div>
      </section>

      {/* Package policies */}
      <section className="py-14 lg:py-16">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <SectionTitle
            eyebrow="Package Policies"
            title="Please read"
            scriptWord="before purchasing"
          />
          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            {policies.map((p, i) => (
              <Reveal
                key={p.title}
                delay={i * 110}
                className="bg-warm-white rounded-3xl p-7 sm:p-8 shadow-soft border border-pale-rose/40 lift-on-hover"
                data-testid={`policy-${i}`}
              >
                <h3 className="font-serif-display text-xl leading-tight">{p.title}</h3>
                <p className="mt-3 font-body text-sm text-charcoal-soft leading-relaxed">{p.copy}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-blush/40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionTitle title="Not sure which session?" scriptWord="Let&rsquo;s chat." />
          <p className="mt-6 font-body text-charcoal-soft leading-relaxed max-w-xl mx-auto">
            Your complimentary consultation is the perfect place to decide together.
          </p>
          <BookLink  className="btn-petal mt-8 inline-flex" data-testid="pricing-cta-book">
            Book Consultation
            <ArrowRight size={14} />
          </BookLink>
        </div>
      </section>
    </div>
  );
}
